export type PersonalFinanceEnvironment = 'Prod' | 'Non-Prod';

export type CreditRating =
  | 'Excellent'
  | 'VeryGood'
  | 'Good'
  | 'Fair'
  | 'Challenged';

export type CreditRatingKey = '0' | '1' | '2' | '3' | '4';

export const creditRatingToKey = Object.fromEntries([
  ['Excellent', '0'],
  ['VeryGood', '1'],
  ['Good', '2'],
  ['Fair', '3'],
  ['Challenged', '4'],
]) as Record<CreditRating, CreditRatingKey>;

export const creditKeyToRating: Record<CreditRatingKey, CreditRating> = {
  '0': 'Excellent',
  '1': 'VeryGood',
  '2': 'Good',
  '3': 'Fair',
  '4': 'Challenged',
};

export interface IAdobeData {
  position: string;
  originPage: string;
}

export interface ICalculatorOverrides {
  monthlyPayment?: string;
  downPayment?: string;
  creditScore?: CreditRatingKey;
  termLength?: string;
}

export interface IPersonalFinanceComponentProps {
  environment: PersonalFinanceEnvironment;
  adobeData: IAdobeData;
  calculatorOverrides?: ICalculatorOverrides;
  compact?: boolean;
  className?: string;
}

export interface IFinanceRateInfo {
  maximumFicoScore: number | null;
  maximumRate: number;
  medianRate: number;
  minimumFicoScore: number | null;
  minimumRate: number;
  type: CreditRating;
}

export interface IFeeSchedule {
  minimum: number;
  maximum: number;
  estimatedFeesMinimum?: number;
  estimatedFeesMaximum: number;
  details?: IFeeScheduleDetails[];
  feeDisclaimer?: string;
}

export interface IFeeScheduleDetails {
  name: string;
  amount: number;
}

export interface ITaxesAndFees {
  taxesAndFees: IFeeSchedule[];
  stateAbbreviation: string;
}

export interface IInlinePreferences {
  prefersLongestTermLength: boolean;
  monthlyPayment: number;
  termLength: number;
  downPayment: number;
  creditRating: CreditRating;
  hasInteraction: boolean;
}

export interface IInlineRequestPreferences {
  prefersLongestTermLength?: boolean | null;
  monthlyPayment?: number | null;
  termLength?: number | null;
  downPayment?: number | null;
  creditRating?: CreditRating | null;
}

export interface ICalculatorVisitorPreferences extends IInlinePreferences {
  visitorId: string;
}

export interface ICalculatorDefaults {
  termLengths: number[];
  preferences: ICalculatorVisitorPreferences;
  rates: IFinanceRateInfo[];
  aprByCreditRating: Record<CreditRating, number>;
}

export interface IPrequalApplication {
  applicationId: string;
  applicantFirstName: string;
  coApplicantFirstName: string | null;
  downPayment: number;
  storeId: string;
  vehiclePrice?: number;
  stockNumber: string | null;
}

export interface IPrequalDecision {
  minApr: number;
  maxApr: number;
  decisionStatus: 'APPROVE' | 'APPCOND' | 'DECLINE' | 'ERROR';
  downPayment: number;
  minMonthlyPayment: number;
  maxMonthlyPayment: number;
  termLength: number;
  callToActionText: string;
}

export interface IPrequalResult {
  status: number;
  application: IPrequalApplication;
  decision: IPrequalDecision;
  metaData: {
    isFbsEligible: boolean;
    isBuyWithCash: boolean;
    decisionedDate: string;
  };
}
